import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './ProgressCarousel.css';

const STEPS = [
  {
    id: 'analyze',
    title: 'Analyze',
    description: 'AI-powered text analysis',
    path: '/admin/analyze',
    icon: '📊'
  },
  {
    id: 'form',
    title: 'Form',
    description: 'Edit and save event details',
    path: '/admin/form',
    icon: '📝'
  },
  {
    id: 'calendar',
    title: 'Calendar',
    description: 'View and manage events',
    path: '/admin',
    icon: '📅'
  }
];

const ProgressCarousel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);

  // Determine current step based on current path
  useEffect(() => {
    let pathIndex = -1;
    
    // Check exact matches first for specific paths
    for (let i = 0; i < STEPS.length; i++) {
      const step = STEPS[i];
      if (location.pathname === step.path) {
        pathIndex = i;
        break;
      }
    }
    
    // If no exact match, check for path prefixes (but be careful with /admin)
    if (pathIndex === -1) {
      if (location.pathname.startsWith('/admin/analyze')) {
        pathIndex = 0; // Analyze
      } else if (location.pathname.startsWith('/admin/form')) {
        pathIndex = 1; // Form
      } else if (location.pathname === '/admin' || location.pathname.startsWith('/admin/')) {
        // For /admin, only match if it's exactly /admin or starts with /admin/ (but not /admin/analyze or /admin/form which were already checked)
        // This catches /admin, /admin/, and other admin routes like /admin/chat
        pathIndex = 2; // Calendar/Dashboard
      }
    }
    
    if (pathIndex !== -1) {
      setCurrentStep(pathIndex);
    }
  }, [location.pathname]);

  const handleStepClick = (step) => {
    navigate(step.path);
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      navigate(STEPS[nextStep].path);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      navigate(STEPS[prevStep].path);
    }
  };

  return (
    <div className="progress-carousel">
      <div className="carousel-container">
        <div className="progress-indicator">
          {STEPS.map((step, index) => (
            <div key={step.id} className="progress-step-wrapper">
              <div
                className={`progress-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                onClick={() => handleStepClick(step)}
              >
                <div className="step-icon">{step.icon}</div>
                <div className="step-content">
                  <div className="step-number">{index + 1}</div>
                  <div className="step-title">{step.title}</div>
                  <div className="step-description">{step.description}</div>
                </div>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`progress-line ${index < currentStep ? 'completed' : ''}`}></div>
              )}
            </div>
          ))}
        </div>

        <div className="carousel-navigation">
          <button 
            className="nav-button prev" 
            onClick={handlePrev}
            disabled={currentStep === 0}
          >
            ← Previous
          </button>
          <button 
            className="nav-button next" 
            onClick={handleNext}
            disabled={currentStep === STEPS.length - 1}
          >
            Next →
          </button>
        </div>

        <div className="carousel-slide">
          <div className="slide-content">
            <div className="slide-icon">{STEPS[currentStep].icon}</div>
            <h3 className="slide-title">{STEPS[currentStep].title}</h3>
            <p className="slide-description">{STEPS[currentStep].description}</p>
            <button 
              className="slide-action-button"
              onClick={() => handleStepClick(STEPS[currentStep])}
            >
              Go to {STEPS[currentStep].title}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressCarousel;

